import { Injectable, signal, TemplateRef, ViewContainerRef, QueryList, inject, computed, effect } from '@angular/core';
import { GridColumn } from './column.interface';
import { GridSettings } from './grid-settings.model';
import { GridColumnData } from './grid-column-data.class';
import { PapaParseExportService, ExportOptions } from '../services/papa-parse-export-service.service';
import { GridTemplateDirective } from './grid-template.directive';
import { GridFormatterService } from '../services/grid-formatter-service.service';
import { TableFilterService, FilterState } from '../services/table-filter-service.service';
import { FilterDescriptor, ComplexFilterDescriptor } from '../utils/filter.util';
import { ToastService } from 'src/app/services/toast.service';
import { GridAggregateService, ColumnAggregate, AggregateResult } from '../services/grid-aggregate-service.service';
import { GridColumnStructureService, ColumnPosition, HeaderStructure } from '../services/grid-column-structure-service.service';
import { GridSelectionHandler } from './grid-selection-handler.model';
import { GridExpansionHandler } from './grid-expansion-handler.model';
import { GridTemplatesHandler } from './grid-template-handler.model';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { getFilterType } from './column-types';
import { GridSort } from './grid-config.types';
// Grid state interface
export interface GridState {
  readonly settings: GridSettings;
  readonly selectedItems: readonly any[];
  readonly expandedRows: { [key: string]: boolean };
  readonly templateMap: Map<string, TemplateRef<any>>;
  readonly loading: boolean;
  readonly globalFilter: string;
  readonly filterState: FilterState;
  readonly filteredData: readonly any[];
}

// Grid events interface
export interface GridEvents {
  readonly selectionChange?: (items: readonly any[]) => void;
  readonly rowSelect?: (event: any) => void;
  readonly rowUnselect?: (event: any) => void;
  readonly columnsChange?: (columns: GridColumnData[]) => void;
  readonly expansionChange?: (expandedRows: { [key: string]: boolean }) => void;
  readonly filterChange?: (filterState: FilterState) => void;
  readonly dataFiltered?: (filteredData: readonly any[]) => void;
}

@Injectable()
export class GridSettingsFacade {
  //#region Injected Services
  private readonly exportService = inject(PapaParseExportService);
  private readonly formatterService = inject(GridFormatterService);
  private readonly filterService = inject(TableFilterService);
  private readonly toastService = inject(ToastService);
  private readonly aggregateService = inject(GridAggregateService);
  private readonly columnStructureService = inject(GridColumnStructureService);
  //#endregion

  //#region State Signals
  
  //#region Core state signals
  private readonly settingsSignal = signal<GridSettings>(new GridSettings());
  private readonly loadingSignal = signal<boolean>(false);
  private readonly globalFilterSignal = signal<string>('');
  private readonly selectedItemsSignal = signal<readonly any[]>([]);
  private readonly expandedRowsSignal = signal<Record<string, boolean>>({});
  private readonly templateMapSignal = signal<Map<string, TemplateRef<any>>>(new Map());
  //#endregion Core state signals

  //#region Read-only accessors
  readonly settings = this.settingsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly globalFilter = this.globalFilterSignal.asReadonly();
  readonly selectedItems = this.selectedItemsSignal.asReadonly();
  readonly expandedRows = this.expandedRowsSignal.asReadonly();
  readonly templateMap = this.templateMapSignal.asReadonly();
  //#endregion Read-only accessors

  //#endregion private state objects

  //#region Handlers Instances
  private templatesHandler = new GridTemplatesHandler();
  private expansionHandler = new GridExpansionHandler();
  private selectionHandler = new GridSelectionHandler();
  //#endregion Handlers Instances
    
  //#region Computed Signals

  // Filter-related observables
  readonly filterState = toSignal(
    this.filterService.filterState$, { 
      initialValue: { 
        columnFilters: new Map(), 
        globalFilter: '', 
        isActive: false,
      },
    },
  );

  readonly hasActiveFilters = toSignal(
    this.filterService.hasActiveFilters$, { 
      initialValue: false,
    },
  );
  
  // Data and column computations
  readonly data = computed(() => this.settings().data);
  readonly visibleColumns = computed(() => this.settings().getVisibleColumns());
  readonly exportableColumns = computed(() => this.settings().getExportableColumns());
  readonly frozenLeftColumns = computed(() => this.settings().getFrozenLeftColumns());
  readonly frozenRightColumns = computed(() => this.settings().getFrozenRightColumns());
  readonly scrollableColumns = computed(() => this.settings().getScrollableColumns());
  readonly sortableColumns = computed(() => this.settings().getSortableColumns());
  readonly frozenColumns = computed(() => this.settings().getAllFrozenColumns());

  // Filtered data computation
  readonly filteredData = computed(() => {
    const settings = this.settings();
    const globalFilter = this.globalFilter();
    
    if (!settings.data?.length) return [];
    let filtered = [...settings.data];
    if (globalFilter.trim()) {
      filtered = this.applyGlobalFilterDirect(filtered, globalFilter, settings.globalFilterFields);
    }

    //return this.filterService.applyFilters(settings.data, settings);
    return filtered;
  });

  private applyGlobalFilterDirect(data: any[], searchTerm: string, fields: readonly string[]): any[] {
    if (!searchTerm.trim() || !fields.length) return data;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return data.filter(item => 
      fields.some(field => {
        const value = this.getNestedValue(item, field);
        return String(value).toLowerCase().includes(lowerSearchTerm);
      }),
    );
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  // Selection computations
  readonly hasSelection = computed(() => this.selectedItems().length > 0);
  readonly selectionCount = computed(() => this.selectedItems().length);

  // Expansion computations  
  readonly hasExpandedRows = computed(() => 
    Object.values(this.expandedRows()).some(expanded => expanded),
  );
  readonly expandedCount = computed(() => 
    Object.values(this.expandedRows()).filter(expanded => expanded).length,
  );

  // Combined state
  readonly state = computed((): GridState => ({
    settings: this.settings(),
    selectedItems: this.selectedItems(),
    expandedRows: this.expandedRows(),
    templateMap: this.templateMap(),
    loading: this.loading(),
    globalFilter: this.globalFilter(),
    filterState: this.filterState(),
    filteredData: this.filteredData(),
  }));

  //#endregion Computed Signals

  //#region Observables for Backwards Compatibility
  // Provide observables for components not yet converted to signals
  readonly settings$ = toObservable(this.settings);
  readonly loading$ = toObservable(this.loading);
  readonly selectedItems$ = toObservable(this.selectedItems);
  readonly filteredData$ = toObservable(this.filteredData);
  readonly hasActiveFilters$ = toObservable(this.hasActiveFilters);
  readonly visibleColumns$ = toObservable(this.visibleColumns);
  readonly state$ = toObservable(this.state);

  readonly globalFilter$ = toObservable(this.globalFilter);
  readonly expandedRows$ = toObservable(this.expandedRows);
  readonly templateMap$ = toObservable(this.templateMap);
  readonly hasSelection$ = toObservable(this.hasSelection);
  readonly selectionCount$ = toObservable(this.selectionCount);
  readonly hasExpandedRows$ = toObservable(this.hasExpandedRows);
  readonly expandedCount$ = toObservable(this.expandedCount);
  readonly filterState$ = toObservable(this.filterState);
  readonly data$ = toObservable(this.data);
  readonly frozenLeftColumns$ = toObservable(this.frozenLeftColumns);
  readonly frozenRightColumns$ = toObservable(this.frozenRightColumns);
  readonly scrollableColumns$ = toObservable(this.scrollableColumns);
  

  //#endregion Observables for Backwards Compatibility


  constructor() {
    // Sync handlers with settings when settings change
    effect(() => {
      const settings = this.settings();
      this.syncHandlersWithSettings(settings);
    });

    // Sync filters when global filter changes
    // effect(() => {
    //   const globalFilterValue = this.globalFilter();
    //   this.filterService.setGlobalFilter(globalFilterValue);
    // });
  }
  //#region Settings Management

  updateSettings(settings: GridSettings): void {
    this.settingsSignal.set(settings);
    this.syncHandlersWithSettings(settings);
    // this.syncFiltersWithSettings(settings);
  }

  getCurrentSettings(): GridSettings {
    return this.settings();
  }

  private syncHandlersWithSettings(settings: GridSettings): void {
    // Update expansion handler
    this.expansionHandler.setDataKey(settings.dataKey);
    this.expansionHandler.setAllowMultiple(settings.rowExpansion.allowMultiple || false);

    // Update selection handler
    this.selectionHandler.setDataKey(settings.dataKey);
    this.selectionHandler.setEnabled(settings.selection.enabled || false);
    this.selectionHandler.setSelectionMode(settings.selection.mode || 'single');
    this.selectionHandler.setSelectionType(settings.selection.selectionType || 'row');
    this.selectionHandler.setShowSelectAll(settings.selection.showSelectAll || true);
    if (settings.selection.preselectedItems && settings.selection.preselectedItems.length > 0) {
      this.selectionHandler.setSelectedItems(settings.selection.preselectedItems);
    }
  }

  setLoading(loading: boolean): void {
    this.loadingSignal.set(loading);
  }

  setGlobalFilter(filter: string): void {
    this.globalFilterSignal.set(filter);
    this.filterService.setGlobalFilter(filter);
  }

  //#endregion Settings Management

  //#region Filter Management

  // Set a column filter
  setColumnFilter(field: string, value: any, operator = 'contains', events?: GridEvents): void {
    this.filterService.setColumnFilter(field, value, operator);
    
    if (events?.filterChange) {
      events.filterChange(this.filterService.getCurrentFilterState());
    }
    
    if (events?.dataFiltered) {
      // Use computed signal value directly
      events.dataFiltered(this.filteredData());
    }
  }

  // Remove a column filter
  removeColumnFilter(field: string, events?: GridEvents): void {
      this.filterService.removeColumnFilter(field);
      
      if (events?.filterChange) {
          events.filterChange(this.filterService.getCurrentFilterState());
      }
  }

  // Clear all column filters
  clearColumnFilters(events?: GridEvents): void {
      this.filterService.clearColumnFilters();
      
      if (events?.filterChange) {
          events.filterChange(this.filterService.getCurrentFilterState());
      }
  }

  // Set a complex filter
  setComplexFilter(filter: ComplexFilterDescriptor, events?: GridEvents): void {
      this.filterService.setComplexFilter(filter);
      
      if (events?.filterChange) {
          events.filterChange(this.filterService.getCurrentFilterState());
      }
  }

  // Clear the complex filter
  clearComplexFilter(events?: GridEvents): void {
      this.filterService.clearComplexFilter();
      
      if (events?.filterChange) {
          events.filterChange(this.filterService.getCurrentFilterState());
      }
  }

  // Clear the global filter
  clearGlobalFilter(events?: GridEvents): void {
      this.globalFilterSignal.set('');
      this.filterService.clearGlobalFilter();
      
      if (events?.filterChange) {
          events.filterChange(this.filterService.getCurrentFilterState());
      }
  }

  // Clear all filters
  clearAllFilters(events?: GridEvents): void {
      this.globalFilterSignal.set('');
      this.filterService.clearAllFilters();
      
      if (events?.filterChange) {
          events.filterChange(this.filterService.getCurrentFilterState());
      }
  }

  // Get current filtered data
  getFilteredData(): readonly any[] {
      return this.filteredData();
  }

  // Get current filter state
  getFilterState(): FilterState {
      return this.filterService.getCurrentFilterState();
  }

  // Sync with PrimeNG table filters
  syncWithPrimeNGFilters(
    filters: any, 
    globalFilterValue?: string, 
    events?: GridEvents,
  ): void {
      this.filterService.syncWithPrimeNGTable(filters, globalFilterValue);
      
      if (globalFilterValue !== undefined) {
          this.globalFilterSignal.set(globalFilterValue);
      }
      
      if (events?.filterChange) {
          events.filterChange(this.filterService.getCurrentFilterState());
      }
  }

  // Apply preset filters
  applyPresetFilter(
    type: 'isEmpty' | 'isNotEmpty' | 'today' | 'thisWeek' | 'thisMonth', 
    field: string, 
    events?: GridEvents,
  ): void {
      this.filterService.applyPresetFilter(type, field);

      if (events?.filterChange) {
          events.filterChange(this.filterService.getCurrentFilterState());
      }
  }

  // Create filter for column based on column configuration
  createColumnFilter(field: string, value: any): FilterDescriptor {
      const column = this.settings().findColumn(field);
      if (!column) {
          return { field, value, operator: 'contains' };
      }
      return this.filterService.createColumnFilter(field, value, column);
  }

  // Export current filter configuration
  exportFilterConfig(): any {
      return this.filterService.exportFilterConfig();
  }

  // Import filter configuration
  importFilterConfig(config: any, events?: GridEvents): void {
      this.filterService.importFilterConfig(config);
      
      // Update global filter subject to match imported state
      const filterState = this.filterService.getCurrentFilterState();
      this.globalFilterSignal.set(filterState.globalFilter);
      
      if (events?.filterChange) {
          events.filterChange(filterState);
      }
  }

  //#endregion Filter Management

  //#region Template Management - (using GridTemplateHandler)

  // set ViewContainerRef reference for GridFormatterService
  setViewContainerRef(viewContainerRef: ViewContainerRef): void {
    this.formatterService.setViewContainerRef(viewContainerRef);
  }

  // update template map in GridTemplateService and update observable
  updateTemplateMap(templates: QueryList<GridTemplateDirective>): void {
    this.templatesHandler.clearTemplate();
    this.templatesHandler.updateTemplateMapForQueryList(templates);
    this.templateMapSignal.set(new Map(this.templatesHandler.templateMap));
  }

  // get TemplateRef by name from handler
  getCellTemplate(templateName?: string): TemplateRef<any> | null {
    return this.templatesHandler.getCellTemplate(templateName);
  }

  // get TemplateRef for row expansion from handler
  getExpandTemplate(): TemplateRef<any> | null {
    const templateName = this.settings().rowExpansion.expandTemplate || 'expandedrow';
    return this.templatesHandler.getExpansionTemplate(templateName);
  }
  //#endregion Template Management - (using GridTemplateHandler)

  //#region Selection Management - (using GridSelectionHandler)

  // select item and update signal
  selectRow(rowData: any): void {
    this.selectionHandler.selectRow(rowData);
    this.selectedItemsSignal.set([...this.selectionHandler.getSelectedItems()]);
  }

  setSelectedItems(items: any[]): void {
    this.selectionHandler.setSelectedItems(items);
    this.selectedItemsSignal.set([...this.selectionHandler.getSelectedItems()]);
  }

  // deselect item and update signal
  deselectRow(rowData: any): void {
    this.selectionHandler.deselectRow(rowData);
    this.selectedItemsSignal.set([...this.selectionHandler.getSelectedItems()]);
  }

  // toggle item selected status and update signal
  toggleRowSelection(rowData: any): void {
    this.selectionHandler.toggleRowSelection(rowData);
    this.selectedItemsSignal.set([...this.selectionHandler.getSelectedItems()]);
  }

  // select all filtered items and update signal
  selectAll(): void {
    const currentData = this.filteredData();
    this.selectionHandler.selectAll(currentData);
    this.selectedItemsSignal.set([...this.selectionHandler.getSelectedItems()]);
  }

  // clear all selected items and update signal
  clearSelection(): void {
    this.selectionHandler.clearSelection();
    this.selectedItemsSignal.set([]);
  }

  // toggle all items selected status and update observable
  toggleSelectAll(): void {
    const currentData = this.filteredData();
    this.selectionHandler.toggleSelectAll(currentData);
    this.selectedItemsSignal.set([...this.selectionHandler.getSelectedItems()]);
  }

  // check if row is in selected items array
  isRowSelected(rowData: any): boolean {
    return this.selectionHandler.isRowSelected(rowData);
  }

  // get current selection
  getSelectedItems(): readonly any[] {
    return this.selectedItems();
  }

  // check if all items are selected
  isAllSelected(): boolean {
    const currentSettings = this.settings();
    return this.selectionHandler.isAllSelected(currentSettings.data);
  }

  // check if any items are selected but not all
  isPartiallySelected(): boolean {
    const currentSettings = this.settings();
    return this.selectionHandler.isPartiallySelected(currentSettings.data);
  }

  // update selected items (selection change), update signal and emit update
  // handleSelectionChange<T = any>(selection: T | T[], events?: GridEvents): void {
  handleSelectionChange(selection: any, events?: GridEvents): void {
    this.selectionHandler.handleSelectionChange(selection);
    this.selectedItemsSignal.set([...this.selectionHandler.getSelectedItems()]);
    
    if (events?.selectionChange) {
      events.selectionChange(this.selectionHandler.getSelectedItems());
    }
  }

  // check if any rows available to select
  hasSelectableRows(): boolean {
      const currentSettings = this.settings();
      return currentSettings.data.length > 0;
  }

  // check if selection column should be shown
  showSelectionColumn(): boolean {
      return this.selectionHandler.shouldShowSelectionColumn();
  }

  // check if selection type is single or multiple
  hasMultipleSelection(): boolean {
      return this.selectionHandler.hasMultipleSelection();
  }

  // check if select all should be shown
  shouldShowSelectAll(): boolean {
      return this.selectionHandler.shouldShowSelectAll();
  }

  // get primeng selection mode equivalent
  getSelectionMode(): 'single' | 'multiple' | null {
      return this.selectionHandler.getPrimeNGSelectionMode();
  }

  // get selection type value
  getSelectionType(): 'button' | 'checkbox' | 'icon' | 'radio' | 'row' {
      return this.selectionHandler.getSelectionType();
  }

  // get the count of the amount of currently selected items
  getSelectionCount(): number {
      return this.selectionHandler.getSelectedItems().length;
  }

  //#endregion Selection Management - (using GridSelectionHandler)

  //#region Expansion Management - (using GridExpansionHandler)

  // get count of rows that are expanded
  getExpandedCount(): number {
      return Object.values(this.expansionHandler.getExpandedRows()).filter(expanded => expanded).length;
  }

  // check if row expansion setup enabled
  isRowExpansionEnabled(): boolean {
      const currentSettings = this.settings();
      return currentSettings.rowExpansion.enabled || false;
  }

  // toggle rows expansion status
  toggleRowExpansion(rowData: any): void {
    this.expansionHandler.toggleRowExpansion(rowData);
    this.expandedRowsSignal.set({ ...this.expansionHandler.getExpandedRows() });
  }

  // toggle all rows' expansion status to true
  expandAllRows(): void {
    const currentData = this.filteredData();
    this.expansionHandler.expandAllRows(currentData);
    this.expandedRowsSignal.set({ ...this.expansionHandler.getExpandedRows() });
  }

  // toggle all rows' expansion status to false
  collapseAllRows(): void {
    this.expansionHandler.collapseAllRows();
    this.expandedRowsSignal.set({});
  }

  // check if row is currently expanded
  isRowExpanded(rowData: any): boolean {
    return this.expansionHandler.isRowExpanded(rowData);
  }

  // get object with values of rows that are expanded
  // getExpandedRows(): Record<string, boolean> {
  getExpandedRows(): { [key: string]: boolean } {
    return this.expansionHandler.getExpandedRows();
  }

  //#endregion Expansion Management - (using GridExpansionHandler)

  //#region Data Management
  
  // add row to data in GridSettings
  addRow(rowData: any): void {
    const currentSettings = this.settings();
    const newSettings = currentSettings.addRow(rowData);
    this.updateSettings(newSettings);
  }

  // remove row from data by index in GridSettings
  removeRow(index: number): void {
    const currentSettings = this.settings();
    const newSettings = currentSettings.removeRow(index);
    this.updateSettings(newSettings);
  }

  // update row in data in GridSettings
  updateRow(index: number, rowData: any): void {
    const currentSettings = this.settings();
    const newSettings = currentSettings.updateRow(index, rowData);
    this.updateSettings(newSettings);
  }

  // set data in GridSettings
  setData(data: readonly any[]): void {
    const currentSettings = this.settings();
    const newSettings = currentSettings.setData(data);
    this.updateSettings(newSettings);
  }

  // refresh data in GridSettings
  refreshData(): void {
    const currentSettings = this.settings();
    this.updateSettings(new GridSettings({ ...currentSettings }));
  }

  //#endregion Data Management

  //#region Column Management

  addColumn(column: GridColumn): void {
    const currentSettings = this.settings();
    const newSettings = currentSettings.addColumn(column);
    this.updateSettings(newSettings);
  }

  removeColumn(field: string): void {
    const currentSettings = this.settings();
    const newSettings = currentSettings.removeColumn(field);
    this.updateSettings(newSettings);
  }

  updateColumn(field: string, updates: Partial<GridColumn>): void {
    const currentSettings = this.settings();
    const newSettings = currentSettings.updateColumn(field, updates);
    this.updateSettings(newSettings);
  }

  setColumns(columns: GridColumn[]): void {
    const currentSettings = this.settings();
    const newSettings = currentSettings.setColumns(columns);
    this.updateSettings(newSettings);
  }

  toggleColumnVisibility(field: string): void {
    const currentSettings = this.settings();
    const newSettings = currentSettings.toggleColumnVisibility(field);
    this.updateSettings(newSettings);
  }

  onColumnsChange(columns: GridColumnData[], events?: GridEvents): void {
    this.setLoading(true);
    const currentSettings = this.settings();
    const newSettings = currentSettings.setColumns(columns);
    this.updateSettings(newSettings);
    this.setLoading(false);
    
    if (events?.columnsChange) {
      events.columnsChange(columns);
    }
  }

  findColumn(field: string): GridColumn | null {
    return this.settings().findColumn(field);
  }

  //#endregion Column Management

  //#region Formatting and Display

  getFieldValue(row: any, column: GridColumn): string {
    const value = this.formatterService.getNestedValue(row, column.field);
    return this.formatterService.formatCellValue(value, column, row);
  }

  // Get formatted field value
  getFilterType(column: GridColumn): string {
    return getFilterType(column.filterType ?? column.type);
  }

  // Concatenate class names
  concatenateClassNames(...classNames: (string | undefined | null)[]): string {
    return classNames.filter(Boolean).join(' ');
  }

  //#endregion Formatting and Display

  //#region Export Management
  // TODO - add method/param to export all or export only filtered
  
  // Export data to CSV (uses filtered data if filters are active)
  exportToCSV(fileName?: string, selectedOnly = false): void {
    const filteredData = this.filteredData();
    const settings = this.settings();
    const dataToExport = selectedOnly ? 
      this.getSelectedItems() : 
      (filteredData.length > 0 ? [...filteredData] : [...settings.data]);

    this.exportService.exportToCSV(
      dataToExport,
      settings.getVisibleColumns(),
      fileName || settings.export.fileName || 'export.csv',
      settings.export.header || 'Export',
      settings.export.renderTemplates || true,
    );

    this.toastService.toastCreate(
      `Export Complete. ${dataToExport.length} records exported to CSV`,
      'Success',
    );
  }

  // Export selected items to CSV
  exportSelectedToCSV(fileName?: string): void {
    this.exportToCSV(fileName, true);
  }

  // Export data to CSV with nested column support and optional aggregates
  exportToCSVUpdated(
    fileName?: string, 
    selectedOnly = false, 
    includeAggregates = false, 
    aggregatePosition: 'top' | 'bottom' = 'bottom',
    aggregateSeparatorRow = true,
    flattenNestedHeaders = true,
  ): void {
    const filteredData = this.filteredData();
    const settings = this.settings();
    const dataToExport = selectedOnly ? 
        this.getSelectedItems() : 
        (filteredData.length > 0 ? [...filteredData] : [...settings.data]);

    const exportOptions: ExportOptions = {
      includeAggregates,
      aggregatePosition: aggregatePosition,
      aggregateSeparatorRow: aggregateSeparatorRow,
      flattenNestedHeaders: flattenNestedHeaders,
      customAggregateLabels: {
        'sum': selectedOnly ? 'Selected Total' : 'Total',
        'avg': selectedOnly ? 'Selected Average' : 'Average',
        'count': selectedOnly ? 'Selected Count' : 'Count',
      },
    };

    if (includeAggregates) {
      this.exportService.exportToCSVWithAggregates(
        dataToExport,
        settings.getVisibleColumns(),
        fileName || settings.export.fileName || 'export.csv',
        exportOptions,
      );
    } else {
      this.exportService.exportToCSVUpdated(
        dataToExport,
        settings.getVisibleColumns(),
        fileName || settings.export.fileName || 'export.csv',
        settings.export.header || 'Export',
        settings.export.renderTemplates || true,
        true,
        exportOptions,
      );
    }

    this.toastService.toastCreate(
      `Export Complete. ${dataToExport.length} records exported to CSV${includeAggregates ? ' with aggregates' : ''}`,
      'Success',
    );
  }

  // Export selected items to CSV with aggregates
  exportSelectedToCSVUpdated(fileName?: string, includeAggregates = false): void {
    this.exportToCSVUpdated(fileName, true, includeAggregates);
  }

  // Export with aggregates - convenience method
  exportWithAggregates(fileName?: string, selectedOnly = false): void {
    this.exportToCSVUpdated(fileName, selectedOnly, true);
  }

  // Check if export with aggregates is available
  hasExportableAggregates(): boolean {
    const settings = this.settings();
    return this.aggregateService.hasColumnAggregates(settings.getVisibleColumns());
  }
  
  // Generate export preview for testing
  generateExportPreview(maxRows = 5, includeAggregates = false): any[] {
    const filteredData = this.filteredData();
    const settings = this.settings();
    const dataToPreview = filteredData.length > 0 ? filteredData : settings.data;

    const exportOptions: ExportOptions = {
      includeAggregates,
      aggregatePosition: 'bottom',
      aggregateSeparatorRow: true,
      flattenNestedHeaders: true,
    };

    return this.exportService.generateExportPreview(
      dataToPreview,
      settings.getVisibleColumns(),
      maxRows,
      exportOptions,
    );
  }

  //#endregion Export Management

  //#region Event Handling

  // Handle row click
  handleRowClick(rowData: any, event: Event, events?: GridEvents): void {
    const settings = this.settings();
    const rowExpansion = settings.rowExpansion;

    // Handle row expansion if enabled and configured for row click
    if (rowExpansion.enabled && rowExpansion.expandOnRowClick) {
      this.toggleRowExpansion(rowData);
    }

    // Handle selection through handler
    const handled = this.selectionHandler.handleRowClick(rowData, event);
    if (handled) {
      this.selectedItemsSignal.set([...this.selectionHandler.getSelectedItems()]);
      if (events?.selectionChange) {
        events.selectionChange(this.selectionHandler.getSelectedItems());
      }
    }
  }

  //#endregion Event Handling

  //#region Utility Methods

  // Get row ID
  getRowId(rowData: any): any {
    const settings = this.settings();
    if (typeof rowData === 'object' && rowData !== null) {
        const dataKey = settings.dataKey ?? 'id';
        return (rowData as Record<string, unknown>)[dataKey] ?? rowData;
    }
    return rowData;
  }
  
  // Reset all state
  reset(): void {
    this.selectionHandler.reset();
    this.expansionHandler.collapseAllRows();
    this.setGlobalFilter('');
    this.clearAllFilters();
    this.setLoading(false);
    
    this.selectedItemsSignal.set([]);
    this.expandedRowsSignal.set({});
  }
  
  // Dispose of resources
  dispose(): void {
    this.reset();
  }

  //#endregion Utility Methods

  //#region Column Aggregates Support
  // Calculate aggregate value for a column
  public calculateColumnAggregate(
      aggregate: ColumnAggregate, 
      column: GridColumn, 
      data: ReadonlyArray<unknown>,
  ): unknown {
      return this.aggregateService.calculateColumnAggregate(aggregate, column, data);
  }
  
  // Calculate all aggregates for a column
  public calculateColumnAggregates(
      column: GridColumn, 
      data: ReadonlyArray<unknown>,
      templateResolver?: (templateName: string) => TemplateRef<unknown> | null,
  ): ReadonlyArray<AggregateResult> {
      return this.aggregateService.calculateColumnAggregates(
          column as GridColumn, 
          data, 
          templateResolver,
      );
  }

  // Format aggregate value for display
  public formatAggregateValue(
      value: unknown, 
      aggregate: ColumnAggregate, 
      column: GridColumn,
  ): string {
      return this.aggregateService.formatAggregateValue(value, aggregate, column);
  }
  
  // Check if any column has aggregates enabled
  public hasColumnAggregates(columns?: ReadonlyArray<GridColumn>): boolean {
      const columnsToCheck = columns ?? this.settings().getVisibleColumns();
      return this.aggregateService.hasColumnAggregates(columnsToCheck);
  }
  
  // Check if a specific column has aggregates
  public hasAggregatesForColumn(column: GridColumn): boolean {
      return this.aggregateService.hasAggregatesForColumn(column);
  }
  
  // Get aggregates for a column
  public getColumnAggregates(column: GridColumn): ReadonlyArray<ColumnAggregate> {
      return this.aggregateService.getColumnAggregates(column);
  }
  
  // Get leaf columns (recursively gets all columns without children)
  public getLeafColumns(columns?: ReadonlyArray<GridColumn>): ReadonlyArray<GridColumn> {
      const columnsToProcess = columns ?? this.settings().getVisibleColumns();
      return this.aggregateService.getLeafColumns(columnsToProcess);
  }
  
  // Create aggregate builder for fluent API
  public createAggregateBuilder() {
      return this.aggregateService.createAggregateBuilder();
  }
  
  // Get template resolver function for aggregate display component
  getTemplateResolver(): (templateName: string) => TemplateRef<unknown> | null {
    return (templateName: string) => this.getCellTemplate(templateName);
  }

  //#endregion Column Aggregates Support

  //#region Column Structure Management

  // Calculate the complete header structure for nested columns
  public calculateHeaderStructure(columns?: ReadonlyArray<GridColumn>): HeaderStructure {
      const columnsToProcess = columns ?? this.getVisibleColumns();
      return this.columnStructureService.calculateHeaderStructure(columnsToProcess);
  }

  // Calculate the maximum nesting depth of columns
  public calculateMaxDepth(columns?: ReadonlyArray<GridColumn>): number {
      const columnsToProcess = columns ?? this.getVisibleColumns();
      return this.columnStructureService.calculateMaxDepth(columnsToProcess);
  }

  // Build header rows for multi-level headers
  public buildHeaderRows(columns?: ReadonlyArray<GridColumn>, maxDepth?: number): GridColumn[][] {
      const columnsToProcess = columns ?? this.getVisibleColumns();
      const depth = maxDepth ?? this.columnStructureService.calculateMaxDepth(columnsToProcess);
      return this.columnStructureService.buildHeaderRows(columnsToProcess, depth);
  }

  // Calculate colspan for a column with children
  public calculateColspan(column: GridColumn): number {
      return this.columnStructureService.calculateColspan(column);
  }

  // Calculate rowspan for a column
  public calculateRowspan(column: GridColumn, maxDepth: number, currentRowIndex: number): number {
      return this.columnStructureService.calculateRowspan(column, maxDepth, currentRowIndex);
  }

  // Get columns for a specific header row and position
  public getColumnsForHeaderRow(
      headerRow: ReadonlyArray<GridColumn | GridColumnData>, 
      position: ColumnPosition,
  ): ReadonlyArray<GridColumn | GridColumnData> {
      return this.columnStructureService.getColumnsForHeaderRow(headerRow, position);
  }

  // Get leaf columns (columns without children) filtered by position
  public getLeafColumnsByPosition(
      position: ColumnPosition,
      columns?: ReadonlyArray<GridColumn>,
  ): ReadonlyArray<GridColumn> {
      const columnsToProcess = columns ?? this.getVisibleColumns();
      return this.columnStructureService.getLeafColumnsByPosition(columnsToProcess, position);
  }

  // Check if columns have nested structure
  public hasNestedColumns(columns?: ReadonlyArray<GridColumn>): boolean {
      const columnsToProcess = columns ?? this.getVisibleColumns();
      return this.columnStructureService.hasNestedColumns(columnsToProcess);
  }

  // Get all parent columns (columns with children)
  public getParentColumns(columns?: ReadonlyArray<GridColumn>): ReadonlyArray<GridColumn> {
      const columnsToProcess = columns ?? this.getVisibleColumns();
      return this.columnStructureService.getParentColumns(columnsToProcess);
  }

  // Find a column by field in nested structure
  public findColumnInStructure(field: string, columns?: ReadonlyArray<GridColumn>): GridColumn | null {
      const columnsToProcess = columns ?? this.settings().columns;
      return this.columnStructureService.findColumn(columnsToProcess, field);
  }

  // Get the path to a column in nested structure
  public getColumnPath(field: string, columns?: ReadonlyArray<GridColumn>): ReadonlyArray<string> {
      const columnsToProcess = columns ?? this.settings().columns;
      return this.columnStructureService.getColumnPath(columnsToProcess, field);
  }

  // Get column level in hierarchy
  public getColumnLevel(field: string, columns?: ReadonlyArray<GridColumn>): number {
      const columnsToProcess = columns ?? this.settings().columns;
      return this.columnStructureService.getColumnLevel(columnsToProcess, field);
  }

  // Validate column structure
  public validateColumnStructure(columns?: ReadonlyArray<GridColumn>): {
      valid: boolean;
      errors: ReadonlyArray<string>;
  } {
      const columnsToProcess = columns ?? this.settings().columns;
      return this.columnStructureService.validateColumnStructure(columnsToProcess);
  }

  // Flatten nested columns into a single array
  public flattenColumns(columns?: ReadonlyArray<GridColumn>): ReadonlyArray<GridColumn> {
      const columnsToProcess = columns ?? this.settings().columns;
      return this.columnStructureService.flattenColumns(columnsToProcess);
  }

  public updateNestedColumn(field: string, updates: Partial<GridColumn>) {
    const currentSettings = this.settings();
    const newSettings = currentSettings.updateNestedColumn(field, updates);
    this.updateSettings(newSettings);
  }

//#endregion Column Structure Management

  //#region Sort Utils
  clearSort() {
    const currentSettings = this.settings();
    const newSettings = currentSettings.clearSort();
    this.updateSettings(newSettings);
  }

  updateMultiSortMeta(multisort: GridSort[]) {
    const currentSettings = this.settings();
    const newSettings = currentSettings.updateMultiSortMeta(multisort);
    this.updateSettings(newSettings);
  }

  getMultiSortMeta(): GridSort[] | null {
    return this.settings().getMultiSortMeta();
  }

  //#endregion Sort Utils

  public getVisibleColumns(): GridColumn[]{
    return this.settings().getVisibleColumns();
  }
}